/* 
eslint-disable no-mixed-operators
*/
import React from 'react';
import  { useServices } from '../../../service';
import dlv from 'dlv';
import dbee from '../../../dbee';
import { first } from '../../../util';
import { useTranslation, scope } from '../../../i18n';

export const context = React.createContext({});

const noOpApi = {
	getUsers: () => ([]),
	getSelectedUser: () => {},
	setUser: () => {},
	deleteUser:() => {},
	getLoggedInUser: () => {}
};

export function UsersProvider({ children , service:servicesDescriptor}) {
	const common_t = scope(useTranslation('common'));
	const db = dbee('users');
	const previouslySelectedUser = db.get('selected');
	const getService = id => first(servicesDescriptor, service => service.id === id) || {id};

	/**
	 * !!Important!!:
	 * 
	 * When the logged in user changes the selected user, we want to do a wholesale 
	 * dump of the previous state. We need to ensure that any dynamic forms that 
	 * may have been populated with data, or screen sequences that were partially 
	 * complete get reset.
	 * 
	 * To do this, we use a unique key for each user choice, and update it when the user changes.
	 * This forces a dom dump that resets everything to it's initial state.
	 * 
	 */
	const [stateKey, setStateKey] = React.useState(0);
	const [selectedUser, setSelectedUser] = React.useState(null);
	const [prevSelectedUser, setPrevSelectedUser] = React.useState(null);

	if(selectedUser && (!prevSelectedUser || selectedUser.userId !== prevSelectedUser.userId)) {
		setPrevSelectedUser(selectedUser);
		if(prevSelectedUser) setStateKey(stateKey + 1);
	}

	const [, call, result] = useServices([getService('GetUsers')], [], { common:common_t });
	React.useEffect(() => {
		call();
	}, []); //eslint-disable-line react-hooks/exhaustive-deps
	const users = dlv(result,'users',[]);
	
	if(previouslySelectedUser && !selectedUser && users) {
		const findUser = users.filter(u => u.userId === previouslySelectedUser);
		if(findUser && findUser.length) setSelectedUser(findUser[0]);
	}

	const [, callDeleteUser] = useServices([getService('DeleteUser')], [], { common:common_t });
	const getLoggedInUser = () => users[0];
	const api = {
		getLoggedInUser,
		getUsers: () => users,
		getSelectedUser: () => selectedUser || getLoggedInUser(),
		setUser: user => {
			db.set('selected', user.userId);
			setSelectedUser(user);
		},
		deleteUser: user => {
			callDeleteUser({userId: user.userId})
			call();
		},
		stateKey:stateKey
	}

	return users && users.length && ( 
			<context.Provider value={{api}}>
				{children}
			</context.Provider>
		) || null;
}

export function wrap(args) {
	return function (Child) {
		function Wrapper(props) {
			return <UsersProvider {...args}><Child {...props}/></UsersProvider>;
		}
		return Wrapper;
	};
}

export function useUsers() {
	return React.useContext(context).api || noOpApi;
}